import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Mail, CheckCircle, Scale, Users, FileSignature, Send, MessageCircle, Star, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO, { generateServiceSchema } from '../components/SEO';
import { serviceAPI, leadAPI, ratingAPI, openWhatsApp } from '../lib/api';
import { useSite } from '../context/SiteContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { getField } from '../lib/localize';
import { trackServiceClick, trackLeadSubmit } from '../hooks/useAnalytics';

// Star rating component - moved outside to prevent re-creation on each render
const StarRating = ({ rating, onRatingChange, readOnly = false, size = 20 }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readOnly && onRatingChange && onRatingChange(star)}
          className={`${readOnly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
          disabled={readOnly}
        >
          <Star
            size={size}
            className={star <= rating ? 'fill-[#FBC02D] text-[#FBC02D]' : 'text-gray-300 dark:text-gray-600'}
          />
        </button>
      ))}
    </div>
  );
};

const ServiceProfile = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(false);
  const { settings } = useSite();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  
  const [ratingForm, setRatingForm] = useState({
    rating: 5,
    review: '',
    reviewer_name: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const serviceRes = await serviceAPI.getBySlug(slug);
        setService(serviceRes.data);
        // Track service view
        trackServiceClick(serviceRes.data.id);
        
        // Fetch ratings using service ID
        const ratingsRes = await ratingAPI.getByService(serviceRes.data.id);
        setRatings(ratingsRes.data);
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
      setLoading(false);
    };
    fetchData();
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error(language === 'en' ? 'Please fill all fields' : 'সব ফিল্ড পূরণ করুন');
      return;
    }
    
    setSubmitting(true);
    try {
      await leadAPI.create({
        ...form,
        service_id: service.id,
      });
      trackLeadSubmit(service.id); // Track lead submission
      toast.success(language === 'en' ? 'Your request has been sent successfully!' : 'আপনার অনুরোধ সফলভাবে পাঠানো হয়েছে!');
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error(language === 'en' ? 'Something went wrong, please try again' : 'কিছু সমস্যা হয়েছে, আবার চেষ্টা করুন');
    }
    setSubmitting(false);
  };

  const handleWhatsApp = () => {
    const message = settings.whatsapp_default_message || `আসসালামু আলাইকুম, ${service.name}। ভূমি প্ল্যাটফর্ম থেকে আপনার সাথে যোগাযোগ করছি।`;
    openWhatsApp(service.whatsapp || service.phone, message);
  };

  const handleRatingSubmit = async (e) => {
    e.preventDefault();
    if (!ratingForm.reviewer_name || ratingForm.rating < 1) {
      toast.error(language === 'en' ? 'Please provide name and rating' : 'নাম এবং রেটিং দিন');
      return;
    }
    
    setRatingSubmitting(true);
    try {
      await ratingAPI.create(service.id, {
        ...ratingForm,
        service_id: service.id,
      });
      setRatingForm({ rating: 5, review: '', reviewer_name: '' });
      setShowRatingForm(false);
      setPendingMessage(true);
      toast.success(language === 'en' ? 'Review submitted! It will appear after admin approval.' : 'রিভিউ জমা হয়েছে! অ্যাডমিন অনুমোদনের পর দেখাবে।');
    } catch (err) {
      toast.error(language === 'en' ? 'Failed to submit review' : 'রিভিউ দিতে সমস্যা হয়েছে');
    }
    setRatingSubmitting(false);
  };

  const categoryLabels = {
    lawyer: language === 'en' ? 'Lawyer / Legal Advisor' : 'উকিল / আইন পরামর্শদাতা',
    agent: language === 'en' ? 'Agent' : 'এজেন্ট',
    document_assistant: language === 'en' ? 'Document Assistant' : 'ডকুমেন্ট সহকারী',
  };

  const categoryIcons = {
    lawyer: Scale,
    agent: Users,
    document_assistant: FileSignature,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A]">
        <Navbar isDark={isDark} />
        <div className="pt-20 md:pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="animate-pulse">
              <div className="h-6 md:h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4" />
              <div className="h-10 md:h-12 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-6" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A]">
        <Navbar isDark={isDark} />
        <div className="pt-20 md:pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-12 md:py-16">
            <Users className="mx-auto text-gray-400 mb-4" size={48} />
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('services.noResults')}</h1>
            <Link to="/services">
              <Button className="bg-[#2E7D32] hover:bg-[#1B5E20]">
                <ArrowLeft size={16} className="mr-2" />
                {t('services.backToList')}
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const IconComponent = categoryIcons[service.category] || Users;
  const serviceName = getField(service, 'name', language);
  const serviceBio = getField(service, 'bio', language);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A]">
      <SEO 
        title={`${serviceName} - ${categoryLabels[service.category]}`}
        description={serviceBio || `${serviceName} - ${language === 'en' ? 'Verified service provider in' : 'যাচাইকৃত সেবাদাতা'} ${getField(service, 'location', language)}`}
        path={`/services/${slug}`}
        type="profile"
        schema={generateServiceSchema(service)}
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className="pt-20 md:pt-24 pb-6 md:pb-8 bg-white dark:bg-[#111] border-b border-gray-200 dark:border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/services" 
            className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-[#2E7D32] mb-4 md:mb-6"
            data-testid="back-link"
          >
            <ArrowLeft size={18} className="mr-2" />
            {t('services.backToList')}
          </Link>

          <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-[#E8F5E9] dark:bg-[#2E7D32]/20 flex items-center justify-center flex-shrink-0">
              <IconComponent className="text-[#2E7D32]" size={36} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 md:gap-3 mb-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white" data-testid="service-name">
                  {getField(service, 'name', language)}
                </h1>
                {service.verified && (
                  <div className="verified-pulse flex items-center gap-1 bg-[#E8F5E9] dark:bg-[#2E7D32]/20 px-2 py-1 rounded-full">
                    <CheckCircle className="text-[#2E7D32]" size={14} />
                    <span className="text-xs text-[#2E7D32] font-medium">{t('services.verified')}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-[#E8F5E9] dark:bg-[#2E7D32]/20 text-[#2E7D32] hover:bg-[#E8F5E9] text-xs md:text-sm">
                  {categoryLabels[service.category]}
                </Badge>
                {service.avg_rating > 0 && (
                  <div className="flex items-center gap-1">
                    <Star size={16} className="fill-[#FBC02D] text-[#FBC02D]" />
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{service.avg_rating}</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">({service.total_reviews})</span>
                  </div>
                )}
              </div>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">{getField(service, 'bio', language)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8 md:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Info */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">{t('contact.contactInfo')}</h2>
              <div className="bg-white dark:bg-[#111] rounded-xl p-4 md:p-6 border border-gray-200 dark:border-white/10 space-y-3 md:space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#E8F5E9] dark:bg-[#2E7D32]/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#2E7D32]" size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('contact.address')}</p>
                    <p className="text-sm md:text-base text-gray-900 dark:text-white font-medium truncate">{getField(service, 'location', language)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#E8F5E9] dark:bg-[#2E7D32]/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#2E7D32]" size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('contact.phone')}</p>
                    <p className="text-sm md:text-base text-gray-900 dark:text-white font-medium">{service.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#E8F5E9] dark:bg-[#2E7D32]/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#2E7D32]" size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('contact.email')}</p>
                    <p className="text-sm md:text-base text-gray-900 dark:text-white font-medium truncate">{service.email}</p>
                  </div>
                </div>
                {service.fee_range && (
                  <div className="pt-3 md:pt-4 border-t border-gray-200 dark:border-white/10">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('services.feeRange')}</p>
                    <p className="text-base md:text-lg font-semibold text-[#2E7D32]">{getField(service, 'fee_range', language)}</p>
                  </div>
                )}
              </div>

              {/* WhatsApp Button */}
              {(service.whatsapp || service.phone) && (
                <Button
                  onClick={handleWhatsApp}
                  className="w-full mt-4 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-lg h-11 md:h-12 text-sm md:text-base"
                  data-testid="whatsapp-button"
                >
                  <MessageCircle size={20} className="mr-2" />
                  {t('services.whatsappMessage')}
                </Button>
              )}
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white mb-4 md:mb-6">{t('contact.title')}</h2>
              <form onSubmit={handleSubmit} className="bg-white dark:bg-[#111] rounded-xl p-4 md:p-6 border border-gray-200 dark:border-white/10">
                <div className="space-y-3 md:space-y-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('contact.name')} *</label>
                    <Input
                      type="text"
                      placeholder={t('contact.name')}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="h-10 md:h-11 text-sm dark:bg-white/5 dark:border-white/10"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('contact.email')} *</label>
                    <Input
                      type="email"
                      placeholder={t('contact.email')}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="h-10 md:h-11 text-sm dark:bg-white/5 dark:border-white/10"
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('contact.phone')} *</label>
                    <Input
                      type="tel"
                      placeholder={t('contact.phone')}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="h-10 md:h-11 text-sm dark:bg-white/5 dark:border-white/10"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('contact.message')} *</label>
                    <Textarea
                      placeholder={t('contact.message')}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      className="text-sm min-h-[100px] dark:bg-white/5 dark:border-white/10"
                      data-testid="service-contact-message"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white h-10 md:h-11 text-sm md:text-base"
                    data-testid="contact-submit"
                  >
                    {submitting ? t('contact.sending') : t('contact.send')}
                    <Send size={16} className="ml-2" />
                  </Button>
                </div>
              </form>
            </div>
          </div>

          {/* Ratings Section */}
          <div className="mt-8 md:mt-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">
                {t('ratings.title')} ({ratings.length})
              </h2>
              <Button
                onClick={() => setShowRatingForm(!showRatingForm)}
                variant="outline"
                className="border-[#2E7D32] text-[#2E7D32] hover:bg-[#2E7D32] hover:text-white"
                data-testid="write-review-btn"
              >
                <Star size={16} className="mr-2" />
                {t('ratings.writeReview')}
              </Button>
            </div>

            {/* Rating Form */}
            {showRatingForm && (
              <form onSubmit={handleRatingSubmit} className="bg-white dark:bg-[#111] rounded-xl p-4 md:p-6 border border-gray-200 dark:border-white/10 mb-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('ratings.rating')}</label>
                    <StarRating
                      rating={ratingForm.rating}
                      onRatingChange={(r) => setRatingForm({ ...ratingForm, rating: r })}
                      size={28}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('ratings.reviewerName')} *</label>
                    <Input
                      type="text"
                      placeholder={t('ratings.reviewerName')}
                      value={ratingForm.reviewer_name}
                      onChange={(e) => setRatingForm({ ...ratingForm, reviewer_name: e.target.value })}
                      className="dark:bg-white/5 dark:border-white/10"
                      data-testid="rating-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('ratings.review')}</label>
                    <Textarea
                      placeholder={t('ratings.review')}
                      value={ratingForm.review}
                      onChange={(e) => setRatingForm({ ...ratingForm, review: e.target.value })}
                      rows={3}
                      className="dark:bg-white/5 dark:border-white/10"
                      data-testid="rating-review"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={ratingSubmitting}
                    className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white"
                    data-testid="rating-submit"
                  >
                    {ratingSubmitting ? t('common.loading') : t('ratings.submit')}
                  </Button>
                </div>
              </form>
            )}

            {/* Reviews List */}
            {pendingMessage && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4 mb-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {language === 'en' 
                    ? 'Your review has been submitted and is pending admin approval. Thank you!'
                    : 'আপনার রিভিউ জমা হয়েছে এবং অ্যাডমিন অনুমোদনের অপেক্ষায় আছে। ধন্যবাদ!'
                  }
                </p>
              </div>
            )}
            {ratings.length === 0 ? (
              <div className="bg-white dark:bg-[#111] rounded-xl p-8 border border-gray-200 dark:border-white/10 text-center">
                <Star size={40} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">{t('ratings.noReviews')}</p>
                <p className="text-sm text-gray-400 dark:text-gray-500">{t('ratings.beFirst')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ratings.map((rating) => (
                  <div key={rating.id} className="bg-white dark:bg-[#111] rounded-xl p-4 md:p-5 border border-gray-200 dark:border-white/10">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E8F5E9] dark:bg-[#2E7D32]/20 flex items-center justify-center flex-shrink-0">
                        <User className="text-[#2E7D32]" size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium text-gray-900 dark:text-white">{rating.reviewer_name}</p>
                          <StarRating rating={rating.rating} readOnly size={14} />
                        </div>
                        {rating.review && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">{rating.review}</p>
                        )}
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                          {new Date(rating.created_at).toLocaleDateString('bn-BD')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceProfile;
